import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { makeRequest } from "../../utils/request";
import { PageLayout } from "../../components/PageLayout";
import { Alert, AlertDescription, Badge, Button, Dialog, DropdownField, Icon, InputField, Spinner, Text, View } from "../../components/base";
import { IconButton } from "../../components/base/IconButton";
import { AdminDetailDrawer, AdminTable, Field, RangePicker, rangeToDates, useAdminFetch } from "../../components/admin";
import type { AdminColumn, RangeKey } from "../../components/admin";
import { formatCount, formatDateShort, formatDollars, formatRelative } from "../../utils/adminFormat";
import type { OrgActivityDetail, OrgActivityRow } from "../../types/admin";
import { AddFundsDialog, OrgAvatar, OrgDetailSections, TIER_OPTIONS } from "./adminOrgShared";

const CreateOrganizationDialog = ({ isOpen, onClose, onCreated }: { isOpen: boolean; onClose: () => void; onCreated: () => void }) => {
  const [name, setName] = useState("");
  const [tier, setTier] = useState("community");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setName("");
    setTier("community");
    setError("");
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Please enter an organization name.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await makeRequest("/v1/organizations", {
        method: "POST",
        body: { name: name.trim(), tier },
      });
      handleClose();
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organization.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="New Organization"
      size="sm"
      onSave={handleSave}
      saveLabel="Create"
      isSaving={isSaving}
    >
      <View className="p-4 flex flex-col gap-4">
        <InputField
          label="Name"
          placeholder="Acme Inc."
          value={name}
          onChange={setName}
        />
        <DropdownField
          label="Tier"
          options={TIER_OPTIONS}
          value={tier}
          onChange={setTier}
        />
        {error && <Text className="text-sm text-red-500">{error}</Text>}
      </View>
    </Dialog>
  );
};

export const AdminOrganizationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeKey>("30d");
  const [searchText, setSearchText] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);

  const selectedId = searchParams.get("id");

  const query = useMemo(() => {
    const { startDate, endDate } = rangeToDates(range);
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    return params.toString();
  }, [range]);

  const {
    data: tableResponse,
    isLoading: isLoadingTable,
    error: tableError,
    refresh: refreshTable,
  } = useAdminFetch<{ data: OrgActivityRow[] }>(`/v1/admin/activity/organizations?${query}`);

  const {
    data: detailResponse,
    isLoading: isLoadingDetail,
    error: detailError,
  } = useAdminFetch<{ data: OrgActivityDetail }>(selectedId ? `/v1/admin/activity/organizations/${selectedId}?${query}` : null);

  const rows = useMemo(() => tableResponse?.data ?? [], [tableResponse]);
  const detail = detailResponse?.data ?? null;

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => row.name?.toLowerCase().includes(q) || row.organizationId.toLowerCase().includes(q));
  }, [rows, searchText]);

  const selectedRow = useMemo(() => rows.find((row) => row.organizationId === selectedId) ?? null, [rows, selectedId]);
  const selectedName = detail?.organization.name ?? selectedRow?.name ?? "Organization";
  const selectedTier = detail?.organization.tier ?? selectedRow?.tier;
  const selectedCreatedAt = detail?.organization.createdAt ?? selectedRow?.createdAt;

  const openDrawer = (organizationId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("id", organizationId);
    setSearchParams(next);
  };

  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next);
  };

  const numberColumn = (key: string, header: string, value: (row: OrgActivityRow) => number, width = "80px"): AdminColumn<OrgActivityRow> => ({
    key,
    header,
    width,
    align: "right",
    render: (row) => formatCount(value(row)),
    sortValue: value,
  });

  const dollarColumn = (key: string, header: string, value: (row: OrgActivityRow) => number, width = "96px"): AdminColumn<OrgActivityRow> => ({
    key,
    header,
    width,
    align: "right",
    render: (row) => <span className={value(row) === 0 ? "text-gray-400 dark:text-gray-500" : ""}>{formatDollars(value(row))}</span>,
    sortValue: value,
  });

  const columns: AdminColumn<OrgActivityRow>[] = [
    {
      key: "organization",
      header: "Organization",
      width: "1.6fr",
      render: (row) => (
        <View className="flex min-w-0 flex-row items-center gap-2.5">
          <OrgAvatar name={row.name} />
          <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{row.name}</Text>
          {row.tier === "enterprise" && <Badge variant="info">Enterprise</Badge>}
        </View>
      ),
      sortValue: (row) => row.name?.toLowerCase() ?? null,
    },
    numberColumn("members", "Members", (row) => row.members, "90px"),
    {
      key: "agents",
      header: "Agents",
      width: "90px",
      align: "right",
      render: (row) => (
        <span>
          {formatCount(row.agentsTotal)}
          {row.agentsCreated > 0 && <span className="text-gray-400 dark:text-gray-500"> +{row.agentsCreated}</span>}
        </span>
      ),
      sortValue: (row) => row.agentsTotal,
    },
    numberColumn("sessions", "Sessions", (row) => row.sessions, "96px"),
    numberColumn("releases", "Releases", (row) => row.releases, "90px"),
    numberColumn("pushes", "Pushes", (row) => row.pushes),
    numberColumn("phoneNumbers", "Numbers", (row) => row.phoneNumbers, "90px"),
    numberColumn("invites", "Invites", (row) => row.invites),
    numberColumn("bugTickets", "Bugs", (row) => row.bugTickets, "70px"),
    dollarColumn("creditsUploaded", "Uploaded", (row) => row.creditsUploaded),
    dollarColumn("creditsSpent", "Spent", (row) => row.creditsSpent),
    {
      key: "lastActivityAt",
      header: "Last active",
      width: "110px",
      align: "right",
      render: (row) => <span className={row.lastActivityAt ? "" : "text-gray-400 dark:text-gray-500"}>{formatRelative(row.lastActivityAt)}</span>,
      sortValue: (row) => (row.lastActivityAt ? new Date(row.lastActivityAt).getTime() : null),
    },
  ];

  const periodLabel = range === "all" ? "All time" : `Last ${range}`;

  return (
    <PageLayout
      title="Organizations"
      description="Development activity and engagement by organization."
      headerAction={
        <View className="flex flex-row items-center gap-3">
          <RangePicker
            value={range}
            onChange={setRange}
          />
          <Button
            variant="brand"
            size="sm"
            onClick={() => setIsCreateOpen(true)}
          >
            <Icon
              name="add"
              size={14}
            />
            New organization
          </Button>
        </View>
      }
    >
      <View className="mt-6 flex flex-col gap-3">
        {tableError && (
          <Alert variant="destructive">
            <AlertDescription>{tableError}</AlertDescription>
          </Alert>
        )}
        <View className="max-w-sm">
          <InputField
            placeholder="Search organizations…"
            value={searchText}
            onChange={setSearchText}
          />
        </View>
        <AdminTable
          columns={columns}
          rows={filteredRows}
          rowKey={(row) => row.organizationId}
          onRowClick={(row) => openDrawer(row.organizationId)}
          selectedKey={selectedId}
          isLoading={isLoadingTable}
          defaultSort={{ key: "sessions", dir: "desc" }}
          emptyState={{
            icon: "storefront",
            title: searchText ? "No organizations match your search" : "No organizations yet",
          }}
        />
      </View>

      {/* Read-only shelf: activity at a glance. Managing the org (settings,
          delete) lives on the dedicated /admin/organizations/:id page. */}
      <AdminDetailDrawer
        isOpen={!!selectedId}
        onClose={closeDrawer}
        width={520}
        title={
          <>
            {selectedName}
            {selectedTier === "enterprise" && (
              <Badge
                variant="info"
                className="ml-2"
              >
                Enterprise
              </Badge>
            )}
          </>
        }
        subtitle={selectedCreatedAt ? `Created ${formatDateShort(selectedCreatedAt)}` : undefined}
        headerActions={
          <>
            <IconButton
              icon="open"
              variant="info"
              title="Open organization page"
              onClick={() => selectedId && navigate(`/admin/organizations/${selectedId}`)}
            />
            <IconButton
              icon="payments"
              variant="info"
              title="Add funds"
              onClick={() => setIsAddFundsOpen(true)}
            />
          </>
        }
      >
        {detailError && (
          <Alert variant="destructive">
            <AlertDescription>{detailError}</AlertDescription>
          </Alert>
        )}

        {isLoadingDetail && !detail ? (
          <View className="flex justify-center py-10">
            <Spinner size={24} />
          </View>
        ) : (
          detail && (
            <OrgDetailSections
              detail={detail}
              periodLabel={periodLabel}
            />
          )
        )}

        <Field
          label="ID"
          mono
        >
          {selectedId}
        </Field>
      </AdminDetailDrawer>

      <CreateOrganizationDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={refreshTable}
      />

      <AddFundsDialog
        isOpen={isAddFundsOpen}
        organizationId={selectedId}
        organizationName={selectedName}
        onClose={() => setIsAddFundsOpen(false)}
      />
    </PageLayout>
  );
};
